'use client'

import { create } from 'zustand'

import type { LanePref } from '@/features/veluvit/types/catalog.types'

/**
 * 화면 상태 — 레퍼런스 app.js 의 전역 S 중 서버와 무관한 값만 모았다.
 * 성향은 전체로 고정하고, 실제 표시 설정만 기기에 저장한다.
 */
export type ChatPersona = { name: string; gender: string; desc: string }

type UiState = {
  pref: LanePref | null
  genre: string
  tag: string
  slide: number
  rankPeriod: string
  colFilter: string
  missionFilter: string
  colAll: boolean
  /** "다음부터 보지 않기"를 고른 비용 안내 */
  costSeen: Record<string, boolean>
  /** 작품별로 고른 시작 상황 번호 */
  selIntro: Record<string, number>
  /** 시작 상황 미리보기 펼침 */
  prevOpen: Record<string, boolean>
  /** 캐릭터 선택 화면에서 고른 페르소나 번호 */
  pick: number
  rnd: number
  /** 작품별 대화 페르소나 (시작 전 설정값) */
  persona: Record<string, ChatPersona>
  /** 작품별 대화 모드 */
  modes: Record<string, string>
  /** 작품별 최근 추천 답장 방향 3개 */
  tones: Record<string, string[]>
  pwCount: number
  appShown: boolean
  onboardShown: boolean
  justStamped: number | null
  tabMini: boolean
  /** PC 에서 상단 내비 밑에 붙은 화면 머리줄(탭 줄 등)의 높이(px) — 상단 내비 유리가 이만큼 더 내려와 그 줄까지 덮는다 */
  barHead: number
  /** PC 왼쪽 패널을 아이콘 줄로 접었는가 — 기기에 남긴다 */
  sideCollapsed: boolean
  /** 페이월이 떠 있는 동안 채팅 본문을 흐린다 */
  chatBlurred: boolean
  /** 마지막으로 본 홈이 스토리 탭인가 — 랭킹 화면에서 어느 탭을 켤지 정한다(레퍼런스 S.ptype) */
  homeStory: boolean
  /** 추천 답장 켜기/끄기 — 기기에 남긴다(크랙처럼 입력창 옆 버튼으로 켜고 끈다) */
  suggestionsOn: boolean
  set: (patch: Partial<UiState>) => void
  setSuggestionsOn: (on: boolean) => void
  setSideCollapsed: (collapsed: boolean) => void
  setPersona: (workId: string, persona: ChatPersona) => void
  setMode: (workId: string, mode: string) => void
  pushTone: (workId: string, tone: string | undefined) => void
  seeCost: (key: string) => void
}

const SUGGESTIONS_KEY = 'chat_suggestions_on'
const SIDE_KEY = 'veluvit:side-collapsed'
function loadSideCollapsed(): boolean {
  try {
    return (
      typeof window !== 'undefined' && localStorage.getItem(SIDE_KEY) === '1'
    )
  } catch {
    return false
  }
}

/** 추천 답장 설정 — 저장값이 없거나 읽지 못하면 켜 둔다 */
function loadSuggestionsOn(): boolean {
  try {
    return typeof window === 'undefined'
      ? true
      : localStorage.getItem(SUGGESTIONS_KEY) !== '0'
  } catch {
    return true
  }
}

export const useUiStore = create<UiState>()((set, get) => ({
  pref: 'both',
  genre: '전체',
  tag: '전체',
  slide: 0,
  rankPeriod: 'popular',
  colFilter: 'all',
  missionFilter: '전체',
  colAll: false,
  costSeen: {},
  selIntro: {},
  prevOpen: {},
  pick: 0,
  rnd: 0,
  persona: {},
  modes: {},
  tones: {},
  pwCount: 0,
  appShown: false,
  onboardShown: false,
  justStamped: null,
  tabMini: false,
  barHead: 0,
  sideCollapsed: loadSideCollapsed(),
  chatBlurred: false,
  homeStory: false,
  suggestionsOn: loadSuggestionsOn(),
  set: (patch) => set(patch),
  setSideCollapsed: (collapsed) => {
    try {
      localStorage.setItem(SIDE_KEY, collapsed ? '1' : '0')
    } catch {
      // 저장하지 못해도 이번 방문 동안은 적용된다
    }
    set({ sideCollapsed: collapsed })
  },
  setSuggestionsOn: (on) => {
    try {
      localStorage.setItem(SUGGESTIONS_KEY, on ? '1' : '0')
    } catch {
      // 저장하지 못해도 이번 방문 동안은 적용된다
    }
    set({ suggestionsOn: on })
  },
  setPersona: (workId, persona) =>
    set({ persona: { ...get().persona, [workId]: persona } }),
  setMode: (workId, mode) => set({ modes: { ...get().modes, [workId]: mode } }),
  pushTone: (workId, tone) => {
    if (!tone) return
    set({
      tones: {
        ...get().tones,
        [workId]: [...(get().tones[workId] || []), tone].slice(-3),
      },
    })
  },
  seeCost: (key) => set({ costSeen: { ...get().costSeen, [key]: true } }),
}))
